/**
 * System Metrics Bar Component
 * Displays four metrics as vertical bars with green color variants
 */

import React, { useMemo } from 'react';
import { useAppSelector } from '../../store/hooks';

interface SystemMetricsBarProps {
  activeEvents: number;
  pendingEvents: number;
  totalGuests: number;
  acceptedInvitations: number;
}

interface MetricItem {
  label: string;
  value: number;
  color: string;
}

interface MetricInput {
  label: string;
  value: number;
}

const SystemMetricsBar: React.FC<SystemMetricsBarProps> = ({
  activeEvents,
  pendingEvents,
  totalGuests,
  acceptedInvitations
}) => {
  const { theme } = useAppSelector((state) => state.ui);

  // Prepare metrics with sorting and color assignment
  const metrics = useMemo(() => {
    // Green color variants from lightest to darkest
    const greenColors = [
      '#86efac', // Lightest green
      '#4ade80', // Light green
      '#22c55e', // Medium green
      '#16a34a'  // Darkest green
    ];

    const metricsArray: MetricInput[] = [
      { label: 'Active Events', value: activeEvents },
      { label: 'Pending Events', value: pendingEvents },
      { label: 'Total Guests', value: totalGuests },
      { label: 'Accepted Invitations', value: acceptedInvitations }
    ];

    // Sort by value (ascending) to assign colors
    const sorted = [...metricsArray].sort((a, b) => a.value - b.value);
    
    // Create a map of original order to sorted order for color assignment
    const colorMap = new Map<number, string>();
    sorted.forEach((metric, index) => {
      const originalIndex = metricsArray.findIndex(
        m => m.label === metric.label && m.value === metric.value
      );
      colorMap.set(originalIndex, greenColors[index]);
    });

    // Assign colors to original order
    return metricsArray.map((metric, index): MetricItem => ({
      ...metric,
      color: colorMap.get(index) || greenColors[0]
    }));
  }, [activeEvents, pendingEvents, totalGuests, acceptedInvitations]);

  // Find max value for proportional bar heights
  const maxValue = useMemo(() => {
    return Math.max(activeEvents, pendingEvents, totalGuests, acceptedInvitations, 1);
  }, [activeEvents, pendingEvents, totalGuests, acceptedInvitations]);

  return (
    <div className="w-full">
      <div className="flex items-end justify-between gap-1 sm:gap-2 lg:gap-4 h-40 sm:h-48 lg:h-64 px-0.5 sm:px-1 lg:px-2">
        {metrics.map((metric, index) => {
          const barHeight = maxValue > 0 ? (metric.value / maxValue) * 100 : 0;
          
          return (
            <div
              key={metric.label}
              className="flex-1 flex flex-col items-center justify-end h-full"
            >
              {/* Metric Value Above Bar */}
              <div className="mb-1 sm:mb-2">
                <span className={`text-xs sm:text-sm lg:text-lg font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {metric.value.toLocaleString()}
                </span>
              </div>

              {/* Vertical Bar */}
              <div
                className="w-full rounded-t-lg transition-all duration-300 hover:opacity-80"
                style={{
                  height: `${barHeight}%`,
                  backgroundColor: metric.color,
                  minHeight: metric.value > 0 ? '8px' : '0'
                }}
                role="progressbar"
                aria-valuenow={metric.value}
                aria-valuemin={0}
                aria-valuemax={maxValue}
                aria-label={`${metric.label}: ${metric.value}`}
              />

              {/* Metric Label Below Bar */}
              <div className="mt-1 sm:mt-2 text-center">
                <span className={`metric-label text-xs font-medium leading-tight ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {metric.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SystemMetricsBar;

